import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { parseRichText, richTextToPlain, type RichRun, sanitizeRichText } from "@probetalab/contracts";
import { Icon, type IconName } from "./icons.js";
import { Button, Sheet } from "./ui.js";

/**
 * Formatted text for `document` analytes and study notes (ADR 0019).
 *
 * The editor is a contentEditable surface driven by the browser's own editing commands — no
 * editor library: the lab needs bold, italics, underline, alignment, lists and super/subscript,
 * which Chrome on the tablet and on the desktop already implements. What it produces is never
 * trusted: every change goes through `sanitizeRichText` (the same allow-list the server applies
 * on write and the report applies on print), and paste is sanitised before it lands.
 *
 * Display never uses innerHTML: `RichTextView` builds React elements from the parsed model.
 */

type Tool = { cmd: string; icon: IconName; label: string; group: number };

const FULL: Tool[] = [
  { cmd: "bold", icon: "bold", label: "Negritas", group: 0 },
  { cmd: "italic", icon: "italic", label: "Cursiva", group: 0 },
  { cmd: "underline", icon: "underline", label: "Subrayado", group: 0 },
  { cmd: "justifyLeft", icon: "alignLeft", label: "Alinear a la izquierda", group: 1 },
  { cmd: "justifyCenter", icon: "alignCenter", label: "Centrar", group: 1 },
  { cmd: "justifyRight", icon: "alignRight", label: "Alinear a la derecha", group: 1 },
  { cmd: "justifyFull", icon: "alignJustify", label: "Justificar", group: 1 },
  { cmd: "insertOrderedList", icon: "listOrdered", label: "Lista numerada", group: 2 },
  { cmd: "insertUnorderedList", icon: "listBullet", label: "Lista con viñetas", group: 2 },
  { cmd: "superscript", icon: "superscript", label: "Superíndice (10³)", group: 3 },
  { cmd: "subscript", icon: "subscript", label: "Subíndice (CO₂)", group: 3 },
];
const COMPACT = FULL.filter((t) => t.group === 0);

/** Editing commands are Chrome's; jsdom and very old browsers have none (the text still edits). */
function exec(cmd: string, arg?: string): boolean {
  const d = document as Document & { execCommand?: (c: string, ui?: boolean, v?: string) => boolean };
  return typeof d.execCommand === "function" ? d.execCommand(cmd, false, arg) : false;
}
function commandState(cmd: string): boolean {
  const d = document as Document & { queryCommandState?: (c: string) => boolean };
  try {
    return typeof d.queryCommandState === "function" ? d.queryCommandState(cmd) : false;
  } catch {
    return false;
  }
}

const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Plain pasted text as paragraphs, one per line. */
function plainToHtml(text: string): string {
  return text
    .split(/\r?\n/)
    .map((l) => `<p>${l.trim() ? escapeHtml(l) : "<br>"}</p>`)
    .join("");
}

export function RichTextEditor({
  value,
  onChange,
  label,
  placeholder,
  compact,
  readOnly,
  autoFocus,
  minHeight,
}: {
  /** Sanitised HTML. */
  value: string;
  /** Always called with sanitised HTML ("" when nothing readable is left). */
  onChange: (html: string) => void;
  label: string;
  placeholder?: string;
  /** Bold, italic and underline only (the study note). */
  compact?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const emitted = useRef<string | null>(null);
  const saved = useRef<Range | null>(null);
  const [active, setActive] = useState<Record<string, boolean>>({});
  const [empty, setEmpty] = useState(() => richTextToPlain(value) === "");
  const tools = compact ? COMPACT : FULL;

  // The DOM is the editor's own state; it is only rewritten when the value changes from outside.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || value === emitted.current) return;
    el.innerHTML = sanitizeRichText(value);
    emitted.current = value;
    setEmpty(richTextToPlain(value) === "");
  }, [value]);

  useEffect(() => {
    if (autoFocus && !readOnly) ref.current?.focus();
  }, [autoFocus, readOnly]);

  const refresh = useCallback(() => {
    const next: Record<string, boolean> = {};
    for (const t of tools) next[t.cmd] = commandState(t.cmd);
    setActive(next);
  }, [tools]);

  // Remember the selection inside the editor: a tap on the toolbar may move focus on a tablet.
  useEffect(() => {
    const onSel = () => {
      const sel = document.getSelection();
      const el = ref.current;
      if (!sel || sel.rangeCount === 0 || !el) return;
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) {
        saved.current = r.cloneRange();
        refresh();
      }
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, [refresh]);

  function emit() {
    const el = ref.current;
    if (!el) return;
    const html = sanitizeRichText(el.innerHTML);
    emitted.current = html;
    setEmpty(html === "");
    onChange(html);
  }

  function apply(cmd: string) {
    const el = ref.current;
    if (!el || readOnly) return;
    const sel = document.getSelection();
    const inside = !!sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer);
    if (document.activeElement !== el) el.focus();
    // Only when the selection left the editor (focus moved on a tablet): the live one is newer.
    if (!inside && saved.current && sel) {
      sel.removeAllRanges();
      sel.addRange(saved.current);
    }
    exec("styleWithCSS", "false");
    exec(cmd);
    emit();
    refresh();
  }

  return (
    <div className={`rte${compact ? " compact" : ""}${readOnly ? " readonly" : ""}`}>
      {!readOnly && (
        <div className="rte-toolbar" role="toolbar" aria-label={`Formato: ${label}`}>
          {tools.map((t, i) => (
            <span key={t.cmd} className="rte-tool-wrap">
              {i > 0 && tools[i - 1]!.group !== t.group && <span className="rte-sep" aria-hidden="true" />}
              <button
                type="button"
                className="rte-tool"
                aria-label={t.label}
                title={t.label}
                aria-pressed={!!active[t.cmd]}
                // Keep the text's selection: the tool must not take focus (finger, S Pen or mouse).
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => apply(t.cmd)}
              >
                <Icon name={t.icon} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div
        ref={ref}
        className={`rte-surface${empty ? " is-empty" : ""}`}
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        aria-readonly={readOnly || undefined}
        data-placeholder={placeholder ?? ""}
        // Password managers stay off a results editor (as on every non-credential field).
        data-1p-ignore="true"
        data-lpignore="true"
        data-bwignore="true"
        data-form-type="other"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        spellCheck
        style={minHeight ? { minHeight } : undefined}
        onFocus={() => exec("defaultParagraphSeparator", "p")}
        onInput={emit}
        onPaste={(e) => {
          // Word, a browser or WhatsApp Web: only the allow-listed subset lands.
          e.preventDefault();
          const html = e.clipboardData.getData("text/html");
          const clean = html ? sanitizeRichText(html) : plainToHtml(e.clipboardData.getData("text/plain"));
          if (!exec("insertHTML", clean)) {
            const el = ref.current;
            if (el) el.innerHTML = sanitizeRichText(el.innerHTML + clean);
          }
          emit();
        }}
        onDrop={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && !e.altKey) {
            const k = e.key.toLowerCase();
            const cmd = k === "b" ? "bold" : k === "i" ? "italic" : k === "u" ? "underline" : null;
            if (cmd) {
              e.preventDefault();
              apply(cmd);
            }
          }
        }}
      />
    </div>
  );
}

function runNode(r: RichRun, key: number): ReactNode {
  if (r.text === "\n") return <br key={key} />;
  let n: ReactNode = r.text;
  if (r.script === "sup") n = <sup>{n}</sup>;
  if (r.script === "sub") n = <sub>{n}</sub>;
  if (r.underline) n = <u>{n}</u>;
  if (r.italic) n = <em>{n}</em>;
  if (r.bold) n = <strong>{n}</strong>;
  return <span key={key}>{n}</span>;
}
const runsNode = (runs: RichRun[]) => (runs.length ? runs.map(runNode) : <br />);

/** Formatted text as React elements, from the sanitising parser (never innerHTML). */
export function RichTextView({ html, className = "", clamp }: { html: string | null | undefined; className?: string; clamp?: number }) {
  const blocks = parseRichText(html);
  return (
    <div className={`rich-view ${className}`} style={clamp ? ({ WebkitLineClamp: clamp } as React.CSSProperties) : undefined} data-clamp={clamp ? "" : undefined}>
      {blocks.map((b, i) =>
        b.type === "p" ? (
          <p key={i} style={b.align !== "left" ? { textAlign: b.align } : undefined}>
            {runsNode(b.runs)}
          </p>
        ) : b.ordered ? (
          <ol key={i}>
            {b.items.map((it, j) => (
              <li key={j} style={it.align !== "left" ? { textAlign: it.align } : undefined}>
                {runsNode(it.runs)}
              </li>
            ))}
          </ol>
        ) : (
          <ul key={i}>
            {b.items.map((it, j) => (
              <li key={j} style={it.align !== "left" ? { textAlign: it.align } : undefined}>
                {runsNode(it.runs)}
              </li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
}

/**
 * The document editor as a sheet. Nothing typed is lost: closing it (✕, Escape, tapping outside)
 * keeps the text in the study; only «Descartar cambios» drops what was typed since it opened.
 * The text is saved with the study (Guardar), like every other value on the screen.
 */
export function DocumentSheet({
  open,
  title,
  subtitle,
  value,
  readOnly,
  onApply,
  onClose,
}: {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  value: string;
  readOnly?: boolean;
  onApply: (html: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState(value);
  const discard = useRef(false);
  useEffect(() => {
    if (open) {
      setText(value);
      discard.current = false;
    }
    // Only when it opens: while open, the sheet owns the text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const changed = text !== value;

  function close() {
    if (!readOnly && !discard.current && changed) onApply(text);
    onClose();
  }

  return (
    <Sheet
      open={open}
      onClose={close}
      title={title}
      wide
      footer={
        readOnly ? (
          <Button variant="primary" onClick={close}>
            Cerrar
          </Button>
        ) : (
          <>
            {changed && (
              <Button
                variant="ghost"
                onClick={() => {
                  discard.current = true;
                  onClose();
                }}
              >
                Descartar cambios
              </Button>
            )}
            <Button variant="primary" icon="check" onClick={close}>
              Listo
            </Button>
          </>
        )
      }
    >
      {subtitle && <div className="muted small">{subtitle}</div>}
      {open && (
        <RichTextEditor
          value={text}
          onChange={setText}
          label={title}
          readOnly={readOnly}
          autoFocus
          minHeight={300}
          placeholder="Escribe el informe. Usa la barra para negritas, cursiva, centrado o listas."
        />
      )}
    </Sheet>
  );
}
